import React, { useState } from 'react';
import { Grid, Pagination, InputAdornment, IconButton } from '@mui/material';
import { Search as SearchIcon, Clear as ClearIcon } from '@mui/icons-material';
import PropTypes from 'prop-types';
import { Box, Typography } from '../atoms';
import { TextField } from '@mui/material';
import { Section } from '../molecules';
import { EmptyState } from '../common';
import ProjectCard from '../projects/ProjectCard';

/**
 * ProjectListView Organism
 * A complete project list view with filtering, search, and pagination
 * 
 * @param {Object} props - Component props
 * @param {Array} props.projects - List of projects to display
 * @param {Object} props.pagination - Pagination information
 * @param {boolean} props.loading - Loading state
 * @param {string} props.error - Error message if any
 * @param {Object} props.filters - Current filters
 * @param {Function} props.onFilterChange - Filter change handler
 * @param {Function} props.onStatusChange - Status change handler for projects
 * @param {Function} props.onEdit - Edit handler for projects
 * @param {Function} props.onDelete - Delete handler for projects
 * @param {Function} props.onClone - Clone handler for projects
 * @param {Function} props.onPageChange - Page change handler
 * @param {React.ReactNode} props.headerAction - Optional action component for the header
 * @param {string} props.title - Section title
 * @param {string} props.emptyStateTitle - Title for empty state
 * @param {string} props.emptyStateDescription - Description for empty state
 * @param {Object} props.sx - Additional styles
 * @returns {React.ReactElement} ProjectListView component
 */
const ProjectListView = ({
  projects = [],
  pagination = { page: 1, totalPages: 1, total: 0 },
  loading = false,
  error = null,
  filters = { status: 'active', search: '', page: 1, limit: 60 },
  onFilterChange,
  onStatusChange,
  onEdit,
  onDelete,
  onClone,
  onPageChange,
  headerAction,
  title = 'Projects',
  emptyStateTitle = 'No projects found',
  emptyStateDescription = 'Try adjusting your search or filters, or create a new project.',
  sx = {},
}) => {
  const [search, setSearch] = useState(filters.search || '');
  
  // Handle search input change
  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };
  
  // Handle search submission
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (onFilterChange) {
      onFilterChange({ ...filters, search, page: 1 });
    }
  };
  
  // Handle clearing search
  const handleClearSearch = () => {
    setSearch('');
    if (onFilterChange) {
      onFilterChange({ ...filters, search: '', page: 1 });
    }
  };
  
  // Handle page change
  const handlePageChange = (e, page) => {
    if (onPageChange) {
      onPageChange(page);
    } else if (onFilterChange) {
      onFilterChange({ ...filters, page });
    }
  };
  
  // Handle status filter change
  const handleStatusChange = (status) => {
    if (onFilterChange) {
      onFilterChange({ ...filters, status, page: 1 });
    }
  };

  return (
    <Section
      title={title}
      subtitle={pagination.total > 0 ? `Showing ${projects.length} of ${pagination.total} projects` : null}
      actions={headerAction}
      sx={sx}
    >
      {/* Filter Bar */}
      <Box 
        sx={{ 
          display: 'none', // Hide the filter bar completely
          alignItems: 'center',
          mb: 3,
          gap: 2,
          justifyContent: 'flex-end'
        }}
      >
        {/* Search Box - We'll hide this since we're using the filter dialog */}
        <Box 
          component="form" 
          onSubmit={handleSearchSubmit}
          sx={{ 
            display: 'none',
            flex: 1,
            maxWidth: { xs: '100%', sm: 400 } 
          }}
        >
          <TextField
            placeholder="Search projects..."
            value={search}
            onChange={handleSearchChange}
            fullWidth
            size="small"
            startAdornment={
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            }
            endAdornment={
              search ? (
                <InputAdornment position="end">
                  <IconButton 
                    onClick={handleClearSearch}
                    edge="end"
                    size="small"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null
            }
          />
        </Box>
        
        {/* Status Tabs */}
        {/* Status Tabs - We'll hide these since we have a dedicated filter dialog now */}
        <Box sx={{ display: 'none' }}>
          <Typography 
            variant="button" 
            sx={{ 
              cursor: 'pointer', 
              fontWeight: filters.status === 'all' ? 600 : 400,
              color: filters.status === 'all' ? 'primary.main' : 'text.secondary',
              '&:hover': { color: 'primary.main' }
            }}
            onClick={() => handleStatusChange('all')}
          >
            All
          </Typography>
          
          <Typography 
            variant="button" 
            sx={{ 
              cursor: 'pointer', 
              fontWeight: filters.status === 'active' ? 600 : 400,
              color: filters.status === 'active' ? 'primary.main' : 'text.secondary',
              '&:hover': { color: 'primary.main' }
            }}
            onClick={() => handleStatusChange('active')}
          >
            Active
          </Typography>
          
          <Typography 
            variant="button" 
            sx={{ 
              cursor: 'pointer', 
              fontWeight: filters.status === 'completed' ? 600 : 400,
              color: filters.status === 'completed' ? 'primary.main' : 'text.secondary',
              '&:hover': { color: 'primary.main' }
            }}
            onClick={() => handleStatusChange('completed')}
          >
            Completed
          </Typography>
          
          <Typography 
            variant="button" 
            sx={{ 
              cursor: 'pointer', 
              fontWeight: filters.status === 'archived' ? 600 : 400,
              color: filters.status === 'archived' ? 'primary.main' : 'text.secondary',
              '&:hover': { color: 'primary.main' }
            }}
            onClick={() => handleStatusChange('archived')}
          >
            Archived
          </Typography>
        </Box>
      </Box>
      
      {/* Project Grid */}
      {projects.length === 0 ? (
        <EmptyState
          title={emptyStateTitle}
          description={emptyStateDescription}
          sx={{
            bgcolor: 'white',
            height: '100%', // Full height of parent container
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            borderRadius: '4px'
          }}
        />
      ) : (
        <Grid container spacing={2} sx={{
          width: '100%',
          maxWidth: '100%',
          bgcolor: 'white',
          flex: 1,
          height: '100%',
          overflow: 'auto',
          margin: 0,
          padding: 2,
          '&::-webkit-scrollbar': {
          width: '8px',
          backgroundColor: 'transparent',
        },
        '&::-webkit-scrollbar-track': {
          borderRadius: '10px',
          backgroundColor: 'rgba(0, 0, 0, 0.05)',
          marginTop: '5px',
          marginBottom: '5px',
        },
        '&::-webkit-scrollbar-thumb': {
          borderRadius: '10px',
          backgroundColor: 'rgba(0, 0, 0, 0.2)',
          '&:hover': {
            backgroundColor: 'rgba(25, 118, 210, 0.4)',
          },
        },
        '&::-webkit-scrollbar-corner': {
          backgroundColor: 'transparent',
        },
        }}>
          {projects.map(project => (
            <Grid
              item
              xs={12} sm={6} md={4} lg={3}
              key={project._id || project.id}
              sx={{ boxSizing: 'border-box', height: '140px', p: 2 }} // Fixed height and padding
            >
              <ProjectCard
                project={project}
                onStatusChange={onStatusChange}
                onEdit={onEdit}
                onDelete={onDelete}
                onClone={onClone}
                loading={loading}
              />
            </Grid>
          ))}
        </Grid>
      )}
      
      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination
            count={pagination.totalPages}
            page={pagination.page}
            onChange={handlePageChange}
            color="primary"
          />
        </Box>
      )}
    </Section>
  );
};

ProjectListView.propTypes = {
  projects: PropTypes.array,
  pagination: PropTypes.shape({
    page: PropTypes.number,
    totalPages: PropTypes.number,
    total: PropTypes.number
  }),
  loading: PropTypes.bool,
  error: PropTypes.string,
  filters: PropTypes.shape({
    status: PropTypes.string,
    search: PropTypes.string,
    page: PropTypes.number,
    limit: PropTypes.number
  }),
  onFilterChange: PropTypes.func,
  onStatusChange: PropTypes.func,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  onClone: PropTypes.func,
  onPageChange: PropTypes.func,
  headerAction: PropTypes.node,
  title: PropTypes.string,
  emptyStateTitle: PropTypes.string,
  emptyStateDescription: PropTypes.string,
  sx: PropTypes.object
};

export default ProjectListView;